// @figma file=pkCGby54zXzTsccXvHqzO6 node=15139:15897 name="Tooltip" set="my-taible-shadcn-studio" verified=2026-03-22

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── TooltipProvider ──────────────────────────────────────────────────────────

/**
 * Provides the tooltip context for all `Tooltip` instances beneath it.
 *
 * **Usage**: Wrap your app root or feature area with `<TooltipProvider>`.
 * A single provider at the top of the tree is sufficient.
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <App />
 * </TooltipProvider>
 * ```
 */
export interface TooltipProviderProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider> {}

const TooltipProvider = TooltipPrimitive.Provider;
TooltipProvider.displayName = 'TooltipProvider';

// ─── Tooltip (root) ───────────────────────────────────────────────────────────

/**
 * Short descriptive label that appears on hover or keyboard-focus of a trigger element.
 *
 * Built on `@radix-ui/react-tooltip` — provides full ARIA compliance:
 * - Role: `tooltip` on the content element (WCAG 1.3.1, 4.1.2)
 * - Keyboard: visible on `Tab` focus — not just hover (WCAG 2.1.1)
 * - Delay: configurable via `delayDuration` on `<TooltipProvider>` (default 400 ms)
 * - Positioning: configurable via `side` and `sideOffset` props on `TooltipContent`
 *
 * **Requires** `<TooltipProvider>` as an ancestor.
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button variant="outline">Hover me</Button>
 *     </TooltipTrigger>
 *     <TooltipContent>Save document</TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 * ```
 */
export interface TooltipProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> {}

const Tooltip = TooltipPrimitive.Root;
Tooltip.displayName = 'Tooltip';

// ─── TooltipTrigger ───────────────────────────────────────────────────────────

export interface TooltipTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> {}

const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Trigger ref={ref} className={cn(className)} {...props} />
));
TooltipTrigger.displayName = 'TooltipTrigger';

// ─── TooltipContent variants ──────────────────────────────────────────────────
//
// Figma component set 15139:15897 — "Tooltip"
// Axes: Style x Position (-> side prop) x Shape (-> shape variant)
//
// Colour mapping (Figma fill -> semantic token):
//   Primary     fill_7IEBN8  #0A0A0A -> bg-foreground       / text-background
//   Secondary   fill_AGCE8V  #F5F5F5 -> bg-muted            / text-foreground  + border-border
//   Info        fill_BHLGFU  #0284C7 -> bg-info             / text-info-foreground
//   Success     fill_I6OZHK  #16A34A -> bg-success          / text-success-foreground
//   Warning     fill_NTZXR7  #D97706 -> bg-warning          / text-warning-foreground
//   destructive fill_DVD5ZQ  #DC2626 -> bg-destructive      / text-destructive-foreground
//
// Shape mapping:
//   Default  borderRadius 8px    -> rounded-[var(--radius)]
//   rounded  borderRadius 9999px -> rounded-full
//
// Layout (all variants): padding 6px 12px -> py-1.5 px-3, text-xs (Tailwind/XS/Regular)

const tooltipContentVariants = cva(
  [
    // Layout — Figma padding: 6px 12px, text-xs
    'z-50 inline-flex max-w-xs items-center px-3 py-1.5 text-xs font-medium',
    // Entry animation — Radix data-state + data-side attributes
    'animate-in fade-in-0 zoom-in-95',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
    'data-[side=bottom]:slide-in-from-top-2',
    'data-[side=left]:slide-in-from-right-2',
    'data-[side=right]:slide-in-from-left-2',
    'data-[side=top]:slide-in-from-bottom-2',
    'duration-150',
    'outline-none',
  ],
  {
    variants: {
      /**
       * Visual style — maps to Figma Style axis.
       * - `primary`     Dark background, light text (default Figma variant)
       * - `secondary`   Muted light background, dark text, with border
       * - `info`        Info blue background
       * - `success`     Success green background
       * - `warning`     Warning amber background
       * - `destructive` Destructive red background
       */
      variant: {
        primary: 'bg-foreground text-background',
        secondary: 'bg-muted text-foreground border border-border',
        info: 'bg-info text-info-foreground',
        success: 'bg-success text-success-foreground',
        warning: 'bg-warning text-warning-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
      },
      /**
       * Shape — maps to Figma Shape axis.
       * - `default`  8px border-radius (var(--radius))
       * - `rounded`  Pill shape (rounded-full)
       */
      shape: {
        default: 'rounded-[var(--radius)]',
        rounded: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      shape: 'default',
    },
  },
);

// ─── TooltipContent ───────────────────────────────────────────────────────────

export interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipContentVariants> {
  /** Which portal container to render into. Defaults to document.body. */
  container?: HTMLElement | null;
}

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, variant, shape, side = 'top', sideOffset = 4, container, ...props }, ref) => (
  <TooltipPrimitive.Portal container={container}>
    <TooltipPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ variant, shape }), className)}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, tooltipContentVariants };
